// src/ryan/components/product-blocks/ApostilaDemoBlock.tsx
// Bloco de demonstração "Apostila Interativa" — conteúdo editável pelo admin.
// Coreografia inspirada em src/components/landing/ApostilasBlock.tsx (não importado, não alterado).

import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Highlighter, MessageSquare, HelpCircle, List } from 'lucide-react';
import { DemoPillar, DemoClosing, DemoHeader, DemoPillarsList, DemoClosingPanel } from './DemoBlockShared';
import './ApostilaDemoBlock.css';

// ── Tipos ──────────────────────────────────────────────────────────────

export interface ApostilaDemoSegment {
    id: string;
    text: string;
    highlight: boolean;
    quiz: boolean;
    note: string;
}

export interface ApostilaDemoQuizOption {
    id: string;
    text: string;
    correct: boolean;
}

export interface ApostilaDemoNavItem {
    id: string;
    title: string;
    progress: number;
    hasQuiz: boolean;
    active: boolean;
}

export interface ApostilaDemoData {
    label: string;
    headline: string;
    subheadline: string;
    breadcrumbSubject: string;
    breadcrumbChapter: string;
    chapterTitle: string;
    segments: ApostilaDemoSegment[];
    quizQuestion: string;
    quizOptions: ApostilaDemoQuizOption[];
    navItems: ApostilaDemoNavItem[];
    pillars: DemoPillar[];
    closing: DemoClosing;
}

export const defaultApostilaDemoData = (): ApostilaDemoData => ({
    label: 'Apostilas',
    headline: 'Uma apostila feita para estudar. Não apenas para ler.',
    subheadline: 'A leitura acontece junto com anotações, destaques e revisões, tudo no mesmo lugar.',
    breadcrumbSubject: 'Neurociência',
    breadcrumbChapter: 'Capítulo 2 · Sinapses Químicas',
    chapterTitle: 'Potencial de Ação',
    segments: [
        { id: 'p1', text: 'O potencial de ação é uma variação rápida e transitória do potencial de membrana.', highlight: false, quiz: false, note: 'Revisar mecanismo molecular.' },
        { id: 'p2', text: ' Ocorre quando a despolarização atinge o limiar de disparo, em torno de −55 mV,', highlight: true, quiz: false, note: '' },
        { id: 'p3', text: ' ativando os canais de sódio voltagem-dependentes.', highlight: false, quiz: true, note: '' },
        { id: 'p4', text: ' A fase de repolarização é mediada pela abertura dos canais de potássio e pelo fechamento dos canais de sódio.', highlight: false, quiz: false, note: '' },
    ],
    quizQuestion: 'Qual canal é responsável pela REPOLARIZAÇÃO do potencial de ação?',
    quizOptions: [
        { id: 'a', text: 'Canal de sódio voltagem-dependente', correct: false },
        { id: 'b', text: 'Canal de potássio voltagem-dependente', correct: true },
        { id: 'c', text: 'Canal de cálcio', correct: false },
    ],
    navItems: [
        { id: 'n1', title: 'Potencial de Ação', progress: 100, hasQuiz: true, active: false },
        { id: 'n2', title: 'Sinapses Químicas', progress: 73, hasQuiz: true, active: true },
        { id: 'n3', title: 'Neurotransmissores', progress: 40, hasQuiz: false, active: false },
        { id: 'n4', title: 'Plasticidade Sináptica', progress: 0, hasQuiz: false, active: false },
    ],
    pillars: [
        { id: 'pil-1', icon: 'ScrollText', title: 'Grifos e notas persistentes', text: 'Selecione qualquer trecho e marque. Os grifos ficam salvos, podem ter comentários e são visíveis em todas as visitas.' },
        { id: 'pil-2', icon: 'ListChecks', title: 'Quiz dentro do conteúdo', text: 'Micro-perguntas integradas ao parágrafo. Você testa o entendimento no momento da leitura.' },
        { id: 'pil-3', icon: 'FileText', title: 'Comentários', text: 'Escreva comentários e dúvidas direto no conteúdo enquanto lê. Eles ficam salvos no seu perfil, prontos pra você revisar quando quiser.' },
    ],
    closing: {
        lead: 'Seu PDF informa.',
        emphasis: 'O Ryan evolui com você.',
        items: [
            'Não é um arquivo estático. É onde seu raciocínio ganha forma.',
            'Tudo o que você registra passa a integrar sua base de conhecimento.',
            'O conteúdo que você lê se transforma automaticamente em revisão ativa.',
            'O material evolui sem apagar sua trajetória.',
        ],
    },
});

// ── Demo interna (choreography) ──────────────────────────────────────────

type ToolbarAction = 'highlight' | 'note' | null;

// Duração da animação de saída do painel de índice — CSS puro (não
// Framer Motion, ver Parte 3), então precisa de um pequeno delay em JS pra
// dar tempo da keyframe de saída rodar antes do elemento sumir do DOM.
const NAV_EXIT_MS = 280;

const ApostilaDemoInner: React.FC<{ data: ApostilaDemoData; active: boolean }> = ({ data, active }) => {
    const highlightTargetId = data.segments.find(s => s.highlight)?.id ?? null;
    const noteTargetId = data.segments.find(s => s.note)?.id ?? null;
    const noteTargetText = data.segments.find(s => s.id === noteTargetId)?.note ?? '';

    const baseSegments = data.segments.map(s => ({
        ...s,
        highlight: s.id === highlightTargetId ? false : s.highlight,
        note: s.id === noteTargetId ? '' : s.note,
    }));

    const [segments, setSegments] = useState(baseSegments);
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarTarget, setToolbarTarget] = useState<string | null>(null);
    const [activeAction, setActiveAction] = useState<ToolbarAction>(null);
    const [noteText, setNoteText] = useState('');
    const [noteVisible, setNoteVisible] = useState(false);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [quizSelected, setQuizSelected] = useState<string | null>(null);
    const [quizAnswered, setQuizAnswered] = useState(false);
    const [showNav, setShowNav] = useState(false);
    // Espelha showNav, mas com atraso na saída (ver useEffect abaixo) — dá
    // tempo da animação CSS de "fechar página" (rpb-apt-nav-out) terminar
    // antes do painel sumir do DOM de verdade.
    const [navMounted, setNavMounted] = useState(false);
    const [navClosing, setNavClosing] = useState(false);

    // layoutId do Framer Motion (Parte 2) — precisa ser único por instância
    // deste componente na página (se houver mais de um bloco de Apostila
    // renderizado ao mesmo tempo, ex: dois ContentBlocks numa mesma
    // RyanProductPage, dois layoutId iguais fariam o Framer Motion tentar
    // compartilhar a animação entre os dois, misturando badge de um card com
    // modal de outro). useId() do React gera um id estável e garantidamente
    // único por instância montada — não precisa de nenhum id vindo de fora
    // (a estrutura de props de ApostilaDemoData não foi alterada).
    const uid = useId();
    const quizLayoutId = `apt-quiz-${uid}`;

    // Único ponto de detecção de prefers-reduced-motion do componente — usado
    // tanto pela transição do Framer Motion (Parte 2) quanto pelo efeito de
    // saída do painel de índice (Parte 3).
    const shouldReduceMotion = useReducedMotion();

    const resetDemo = useCallback(() => {
        setSegments(baseSegments);
        setShowToolbar(false);
        setToolbarTarget(null);
        setActiveAction(null);
        setNoteText('');
        setNoteVisible(false);
        setShowQuizModal(false);
        setQuizSelected(null);
        setQuizAnswered(false);
        setShowNav(false);
        setNavMounted(false);
        setNavClosing(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!active) { resetDemo(); return; }

        const timers: ReturnType<typeof setTimeout>[] = [];
        let cursor = 400;

        if (highlightTargetId) {
            timers.push(setTimeout(() => { setToolbarTarget(highlightTargetId); setShowToolbar(true); }, cursor));
            cursor += 600;
            timers.push(setTimeout(() => { setActiveAction('highlight'); }, cursor));
            cursor += 400;
            timers.push(setTimeout(() => {
                setSegments(prev => prev.map(s => s.id === highlightTargetId ? { ...s, highlight: true } : s));
                setShowToolbar(false); setActiveAction(null); setToolbarTarget(null);
            }, cursor));
            cursor += 1300;
        }

        if (noteTargetId && noteTargetText) {
            timers.push(setTimeout(() => { setToolbarTarget(noteTargetId); setShowToolbar(true); }, cursor));
            cursor += 600;
            timers.push(setTimeout(() => {
                setActiveAction('note'); setShowToolbar(false); setNoteVisible(true);
            }, cursor));
            cursor += 300;

            const typingStart = cursor;
            noteTargetText.split('').forEach((char, i) => {
                timers.push(setTimeout(() => { setNoteText(prev => prev + char); }, typingStart + i * 45));
            });
            cursor = typingStart + noteTargetText.length * 45 + 500;

            timers.push(setTimeout(() => {
                setSegments(prev => prev.map(s => s.id === noteTargetId ? { ...s, note: noteTargetText } : s));
                setNoteVisible(false); setActiveAction(null);
            }, cursor));
            cursor += 1300;
        }

        if (data.quizQuestion && data.quizOptions.length > 0) {
            const correctId = data.quizOptions.find(o => o.correct)?.id ?? data.quizOptions[0].id;
            timers.push(setTimeout(() => { setShowQuizModal(true); }, cursor));
            cursor += 800;
            timers.push(setTimeout(() => { setQuizSelected(correctId); }, cursor));
            cursor += 500;
            timers.push(setTimeout(() => { setQuizAnswered(true); }, cursor));
            cursor += 1300;
            timers.push(setTimeout(() => { setShowQuizModal(false); setQuizSelected(null); setQuizAnswered(false); }, cursor));
            cursor += 900;
        }

        if (data.navItems.length > 0) {
            timers.push(setTimeout(() => { setShowNav(true); }, cursor));
            cursor += 1800;
            timers.push(setTimeout(() => { setShowNav(false); }, cursor));
        }

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, data]);

    // Efeito de "página" do painel de índice (Parte 3) — só cuida de QUANDO
    // desmontar o painel em relação a showNav, dando tempo da animação CSS de
    // saída (rpb-apt-nav-out) rodar. Não mexe em nada da cascata de timers
    // acima: showNav continua ligado/desligado exatamente nos mesmos
    // instantes de sempre, isto só reage a essa mudança.
    useEffect(() => {
        if (showNav) {
            setNavMounted(true);
            setNavClosing(false);
            return;
        }
        if (!navMounted) return;
        if (shouldReduceMotion) { setNavMounted(false); return; }
        setNavClosing(true);
        const t = setTimeout(() => { setNavMounted(false); setNavClosing(false); }, NAV_EXIT_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showNav, shouldReduceMotion]);

    return (
        <div className="rpb-apt-panel">
            <div className="rpb-apt-mock-header">
                <span className="rpb-apt-mock-logo">Graia Medicina</span>
                <span className="rpb-apt-mock-sep" />
                <span className="rpb-apt-mock-subtitle">Apostilas</span>
            </div>

            <div className="rpb-apt-reader-body">
                {(data.breadcrumbSubject || data.breadcrumbChapter) && (
                    <div className="rpb-apt-breadcrumb">
                        {data.breadcrumbSubject && <span>{data.breadcrumbSubject}</span>}
                        {data.breadcrumbSubject && data.breadcrumbChapter && <span className="rpb-apt-breadcrumb-sep">›</span>}
                        {data.breadcrumbChapter && <span className="rpb-apt-breadcrumb-active">{data.breadcrumbChapter}</span>}
                    </div>
                )}

                <div className="rpb-apt-reader-text">
                    {data.chapterTitle && <h3 className="rpb-apt-chapter-title">{data.chapterTitle}</h3>}

                    <p className="rpb-apt-paragraph">
                        {segments.map(seg => (
                            <span
                                key={seg.id}
                                className={[
                                    'rpb-apt-seg',
                                    seg.highlight ? 'rpb-apt-seg--highlight' : '',
                                    seg.note ? 'rpb-apt-seg--noted' : '',
                                    toolbarTarget === seg.id ? 'rpb-apt-seg--selecting' : '',
                                ].join(' ')}
                            >
                                {seg.text}
                                {seg.note && (
                                    <span className="rpb-apt-note-marker" title={seg.note}>
                                        <MessageSquare size={9} />
                                    </span>
                                )}
                                {seg.quiz && (
                                    // AnimatePresence próprio (padrão do Framer Motion pra shared
                                    // layout: badge e modal, mais abaixo, cada um dentro do seu
                                    // AnimatePresence, watchando o MESMO layoutId) — o badge só
                                    // existe no DOM enquanto o modal não existe, nunca os dois ao
                                    // mesmo tempo, senão o Framer Motion não sabe qual é a origem e
                                    // qual é o destino do morph.
                                    <AnimatePresence>
                                        {!showQuizModal && (
                                            <motion.button
                                                layoutId={quizLayoutId}
                                                className="rpb-apt-quiz-badge"
                                                onClick={() => setShowQuizModal(true)}
                                                transition={{ duration: shouldReduceMotion ? 0 : 0.4, ease: [0.4, 0, 0.2, 1] }}
                                            >
                                                Quiz
                                            </motion.button>
                                        )}
                                    </AnimatePresence>
                                )}
                            </span>
                        ))}
                    </p>
                </div>

                {showToolbar && (
                    <div className="rpb-apt-toolbar">
                        <button className={`rpb-apt-toolbar-btn ${activeAction === 'highlight' ? 'rpb-apt-toolbar-btn--active' : ''}`}>
                            <Highlighter size={12} /> Grifar
                        </button>
                        <button className={`rpb-apt-toolbar-btn ${activeAction === 'note' ? 'rpb-apt-toolbar-btn--active' : ''}`}>
                            <MessageSquare size={12} /> Nota
                        </button>
                        <button className="rpb-apt-toolbar-btn">
                            <HelpCircle size={12} /> Quiz
                        </button>
                    </div>
                )}

                {noteVisible && (
                    <div className="rpb-apt-note-balloon">
                        <p className="rpb-apt-note-balloon-label">Adicionar nota</p>
                        <div className="rpb-apt-note-input">
                            {noteText}
                            <span className="rpb-apt-note-cursor" />
                        </div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showQuizModal && data.quizOptions.length > 0 && (
                    <motion.div
                        className="rpb-apt-quiz-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
                    >
                        {/* Container externo — só ele faz a transição de shared element
                            (posição/tamanho, via layoutId compartilhado com o badge acima).
                            O Framer Motion resolve o FLIP sozinho, sem cálculo manual de
                            posição. */}
                        <motion.div
                            layoutId={quizLayoutId}
                            className="rpb-apt-quiz-modal"
                            transition={{ duration: shouldReduceMotion ? 0 : 0.4, ease: [0.4, 0, 0.2, 1] }}
                        >
                            {/* Conteúdo interno com fade próprio, independente do morph do
                                container — evita a pergunta/alternativas tentando "crescer
                                junto" pixel a pixel, o que ficaria confuso. */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{
                                    duration: shouldReduceMotion ? 0 : 0.18,
                                    delay: shouldReduceMotion ? 0 : 0.12,
                                }}
                            >
                                <div className="rpb-apt-quiz-modal-header">
                                    <HelpCircle size={13} /> Quiz contextual
                                </div>
                                <p className="rpb-apt-quiz-question">{data.quizQuestion}</p>
                                <div className="rpb-apt-quiz-options">
                                    {data.quizOptions.map((opt, i) => (
                                        <button
                                            key={opt.id}
                                            className={[
                                                'rpb-apt-quiz-option',
                                                quizSelected === opt.id && !quizAnswered ? 'rpb-apt-quiz-option--selected' : '',
                                                quizAnswered && opt.correct ? 'rpb-apt-quiz-option--correct' : '',
                                                quizAnswered && quizSelected === opt.id && !opt.correct ? 'rpb-apt-quiz-option--wrong' : '',
                                            ].join(' ')}
                                            onClick={() => !quizAnswered && setQuizSelected(opt.id)}
                                            disabled={quizAnswered}
                                        >
                                            <span className="rpb-apt-quiz-key">{String.fromCharCode(65 + i)}</span>
                                            <span className="rpb-apt-quiz-opt-text">{opt.text}</span>
                                        </button>
                                    ))}
                                </div>
                                {!quizAnswered ? (
                                    <button className="rpb-apt-quiz-submit" disabled={!quizSelected} onClick={() => setQuizAnswered(true)}>
                                        Responder
                                    </button>
                                ) : (
                                    <button className="rpb-apt-quiz-close" onClick={() => { setShowQuizModal(false); setQuizSelected(null); setQuizAnswered(false); }}>
                                        Continuar lendo
                                    </button>
                                )}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {navMounted && data.navItems.length > 0 && (
                <div className={`rpb-apt-nav-panel${navClosing ? ' rpb-apt-nav-panel--closing' : ''}`}>
                    <div className="rpb-apt-nav-header"><List size={13} /> Índice</div>
                    {data.navItems.map(item => (
                        <div key={item.id} className={`rpb-apt-nav-item ${item.active ? 'rpb-apt-nav-item--active' : ''}`}>
                            <div className="rpb-apt-nav-item-left">
                                <span className="rpb-apt-nav-item-title">{item.title}</span>
                                <div className="rpb-apt-nav-progress-track">
                                    <div className="rpb-apt-nav-progress-fill" style={{ width: `${item.progress}%` }} />
                                </div>
                            </div>
                            {item.hasQuiz && <span className="rpb-apt-nav-quiz-icon"><HelpCircle size={10} /></span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Componente público ────────────────────────────────────────────────

const ApostilaDemoBlock: React.FC<{ data: ApostilaDemoData }> = ({ data }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="rpb-demo-wrap" ref={sectionRef}>
            <DemoHeader label={data.label} headline={data.headline} subheadline={data.subheadline} visible={visible} />
            <div className="rpb-demo-grid">
                <div className="rpb-demo-col-demo">
                    <div className={`rpb-apt-shell ${visible ? 'rpb-apt-shell--visible' : ''}`}>
                        <ApostilaDemoInner data={data} active={visible} />
                    </div>
                </div>
                <div className="rpb-demo-col-pillars">
                    <DemoPillarsList pillars={data.pillars} visible={visible} />
                </div>
            </div>
            <DemoClosingPanel closing={data.closing} visible={visible} />
        </div>
    );
};

export default ApostilaDemoBlock;
